import { useId, useRef } from 'react';
import { symbolShortcut } from '@peruselab/structure-editor/domain/symbolShortcuts';
import { isFormulaWithCounts } from '@peruselab/structure-editor/chemistry/reactionText';
import { useTranslation } from 'react-i18next';
import { durationUnitFor, SECONDS_PER, type ConditionFields, type DurationUnit } from '../../hooks/reactionConditions';
import type { ReactionTextReading } from '../../hooks/useReactionTextReading';
import { Icon, type IconName } from '../Icon';
import { SymbolPicker } from '../SymbolPicker';
import { FormulaText } from '../FormulaText';

const NUMBER_FIELDS: readonly {
  readonly field: Exclude<keyof ConditionFields, 'notes' | 'durationUnit'>;
  /** #1137 -- the same value as the texts by the arrow give it, in the card's unit. */
  readonly fromText: keyof ReactionTextReading['conditions'];
  readonly min?: number;
  readonly max?: number;
}[] = [
  { field: 'temperature', fromText: 'temperatureC' },
  { field: 'pressure', fromText: 'pressureAtm', min: 0 },
  { field: 'duration', fromText: 'durationS', min: 0 },
  { field: 'yieldPercent', fromText: 'yieldPercent', min: 0, max: 100 }
];

/** A value as the card shows it: whole, or to three decimals. */
const shown = (value: number) => String(Math.round(value * 1000) / 1000);

const DURATION_UNITS: readonly DurationUnit[] = ['min', 'h', 's'];

const LOOKUP_ICON: Record<string, IconName> = { found: 'check', looking: 'search', 'not-found': 'help' };

/**
 * #705 (ADR 0014 §3) -- the Conditions card under the drawing paper while it holds a reaction
 * arrow: the arrow's conditions (temperature, pressure, duration, yield, notes), saved with the
 * reaction. #1137 (ADR 0014 amendment) -- and what the texts by the arrow say: a field left empty
 * shows (and saves) the paper's value; one typed in that differs says what the paper says; the
 * reagents typed there are listed with their lookups, and the words for how as notes.
 */
export function ReactionConditionsCard({
  fields,
  onChange,
  fromText
}: {
  readonly fields: ConditionFields;
  readonly onChange: (field: keyof ConditionFields, value: string) => void;
  readonly fromText?: ReactionTextReading | undefined;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const notesId = useId();
  const notesRef = useRef<HTMLTextAreaElement>(null);
  /** Puts the Notes caret at `offset` once the new value is in the field. */
  const placeCaret = (offset: number) =>
    requestAnimationFrame(() => {
      notesRef.current?.focus();
      notesRef.current?.setSelectionRange(offset, offset);
    });
  /** A symbol from the grid, typed at the Notes caret (over its selection). */
  const typeIntoNotes = (symbol: string) => {
    const start = notesRef.current?.selectionStart ?? fields.notes.length;
    const end = notesRef.current?.selectionEnd ?? fields.notes.length;
    onChange('notes', fields.notes.slice(0, start) + symbol + fields.notes.slice(end));
    placeCaret(start + symbol.length);
  };
  return (
    <section className="reaction-conditions-card" aria-labelledby={headingId} data-testid="reaction-conditions-card">
      <h2 id={headingId} className="reaction-conditions-heading">
        {t('reactionConditions.heading')}
      </h2>
      <div className="reaction-conditions-grid">
        {NUMBER_FIELDS.map(({ field, fromText: key, min, max }) => {
          const typed = fields[field].trim();
          const seconds = fromText?.conditions[key];
          // Duration is typed in a unit (#1137 follow-up); empty, it shows the paper's in the unit that
          // reads best, and typing keeps that unit.
          const unit: DurationUnit | null =
            field !== 'duration'
              ? null
              : typed === '' && seconds !== undefined
                ? durationUnitFor(seconds)
                : fields.durationUnit;
          const paper = seconds === undefined ? undefined : unit ? seconds / SECONDS_PER[unit] : seconds;
          const differs = paper !== undefined && typed !== '' && Math.abs(Number(typed) - paper) > 1e-6;
          const input = (
            <input
              type="number"
              inputMode="decimal"
              {...(min !== undefined ? { min } : {})}
              {...(max !== undefined ? { max } : {})}
              value={fields[field]}
              placeholder={paper !== undefined ? shown(paper) : undefined}
              title={paper !== undefined && typed === '' ? t('reactionConditions.fromPaper') : undefined}
              onChange={(event) => {
                if (unit && unit !== fields.durationUnit) onChange('durationUnit', unit);
                onChange(field, event.target.value);
              }}
            />
          );
          return (
            <label
              key={field}
              className={paper !== undefined && typed === '' ? 'reaction-conditions-from-text' : undefined}
            >
              <span>{t(`reactionConditions.${field}`)}</span>
              {unit ? (
                <span className="reaction-conditions-with-unit">
                  {input}
                  <select
                    aria-label={t('reactionConditions.durationUnit')}
                    value={unit}
                    onChange={(event) => onChange('durationUnit', event.target.value)}
                  >
                    {DURATION_UNITS.map((option) => (
                      <option key={option} value={option}>
                        {t(`reactionConditions.unit.${option}`)}
                      </option>
                    ))}
                  </select>
                </span>
              ) : (
                input
              )}
              {differs ? (
                <small className="reaction-conditions-differs" role="note">
                  {t('reactionConditions.paperSays', {
                    value: unit ? `${shown(paper)} ${t(`reactionConditions.unit.${unit}`)}` : shown(paper)
                  })}
                </small>
              ) : null}
            </label>
          );
        })}
        <div className="reaction-conditions-notes">
          <span className="reaction-conditions-notes-heading">
            <label htmlFor={notesId}>{t('reactionConditions.notes')}</label>
            <SymbolPicker onInsert={(symbol) => typeIntoNotes(symbol)} />
          </span>
          <textarea
            ref={notesRef}
            id={notesId}
            rows={2}
            value={fields.notes}
            onChange={(event) => {
              const { value, selectionStart, selectionEnd } = event.target;
              // #1137 follow-up -- one character typed that completes a shortcut ("->", "\Delta ").
              const shortcut =
                selectionStart === selectionEnd && value.length === fields.notes.length + 1
                  ? symbolShortcut(value.slice(0, selectionStart))
                  : null;
              if (!shortcut) {
                onChange('notes', value);
                return;
              }
              const start = selectionStart - shortcut.length;
              onChange('notes', value.slice(0, start) + shortcut.symbol + value.slice(selectionStart));
              placeCaret(start + shortcut.symbol.length);
            }}
          />
        </div>
      </div>
      {fromText && (fromText.reagents.length > 0 || fromText.notes.length > 0) ? (
        <div className="reaction-conditions-paper" data-testid="reaction-conditions-from-paper">
          <h3>{t('reactionConditions.fromPaperHeading')}</h3>
          <ul>
            {fromText.reagents.map((reagent) => (
              <li key={`${reagent.annotationId}-${reagent.name}`} data-lookup={reagent.lookup.status}>
                <Icon name={LOOKUP_ICON[reagent.lookup.status]!} size="1rem" />
                <span className="reaction-conditions-reagent">
                  {isFormulaWithCounts(reagent.name) ? <FormulaText formula={reagent.name} /> : reagent.name}
                </span>
                <span className="reaction-conditions-reagent-status">
                  {reagent.lookup.status === 'found'
                    ? t(
                        `reactionConditions.role.${reagent.zone === 'below' ? 'solvent' : reagent.catalyst ? 'catalyst' : 'reagent'}`
                      )
                    : reagent.lookup.status === 'looking'
                      ? t('reactionConditions.lookingUp')
                      : t('reactionConditions.notFound')}
                </span>
              </li>
            ))}
            {fromText.notes.length > 0 ? (
              <li data-lookup="note">
                <Icon name="stickyNote" size="1rem" />
                <span>{t('reactionConditions.textNotes', { notes: fromText.notes.join(', ') })}</span>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
